import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Twitter, Save, Send } from "lucide-react";
import { toast } from "sonner";

const KEY_FIELDS = [
  { k: "consumer_key", label: "API Key (Consumer Key)" },
  { k: "consumer_secret", label: "API Key Secret" },
  { k: "access_token", label: "Access Token" },
  { k: "access_secret", label: "Access Token Secret" },
];

export default function XPosterCard() {
  const [cfg, setCfg] = useState(null);
  const [keys, setKeys] = useState({});
  const [interval, setIntervalH] = useState(6);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/x/config");
      setCfg(data);
      setIntervalH(data.interval_hours);
      setEnabled(data.enabled);
    } catch { setCfg({}); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { interval_hours: Number(interval) || 6, enabled };
      KEY_FIELDS.forEach(({ k }) => { if ((keys[k] || "").trim()) body[k] = keys[k].trim(); });
      await api.put("/x/config", body);
      setKeys({});
      toast.success("X poster settings saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally { setBusy(false); }
  };

  const postFlyer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/x/post-flyer");
      toast.success(`Flyer posted to X${data.tweet_id ? ` (tweet ${data.tweet_id})` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Post failed"); } finally { setBusy(false); }
  };
  const postNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/x/post-now");
      toast.success(`Posted ad ${data.ad_code} to X${data.tweet_id ? ` (tweet ${data.tweet_id})` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  const clearKeys = async () => {
    try {
      await api.put("/x/config", { clear_keys: true });
      toast.success("X API keys removed");
      load();
    } catch { toast.error("Could not remove keys"); }
  };

  if (!cfg) return null;

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="x-poster-card">
      <div className="flex items-center gap-2">
        <Twitter className="h-4 w-4 text-white" />
        <h2 className="font-black text-xl tracking-tight">X (Twitter) auto-poster</h2>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${enabled ? "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10" : "text-neutral-500 border-white/10 bg-white/5"}`} data-testid="x-status-badge">
          {enabled ? "on" : "off"}
        </span>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Posts your active adverts to your VISMARKS X account. Get keys at <span className="font-mono text-neutral-400">developer.x.com</span> → your app → Keys and tokens (app needs Read + Write permission).
      </p>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        {KEY_FIELDS.map(({ k, label }) => (
          <input key={k} type="password" value={keys[k] || ""} onChange={(e) => setKeys({ ...keys, [k]: e.target.value })}
            placeholder={cfg.keys_set ? `${label} saved ✓ — paste to replace` : label}
            data-testid={`x-${k.replace(/_/g, "-")}-input`}
            className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-white" />
        ))}
        <div className="flex items-center gap-3">
          <label className="text-xs text-neutral-400">Post every</label>
          <input type="number" min={1} max={168} value={interval} onChange={(e) => setIntervalH(e.target.value)}
            data-testid="x-interval-input"
            className="h-10 w-20 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-white" />
          <span className="text-xs text-neutral-400">hours</span>
          <label className="ml-4 flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="x-enabled-toggle" className="accent-white" />
            Auto-posting enabled
          </label>
        </div>
        <div className="flex items-center gap-2 justify-end">
          {cfg.keys_set && (
            <button onClick={clearKeys} data-testid="x-clear-keys-btn"
              className="h-10 px-3 rounded-full border border-[#27272A] text-xs text-neutral-500 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors">
              Remove keys
            </button>
          )}
          <button onClick={save} disabled={busy} data-testid="x-save-btn"
            className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-[#27272A] text-sm font-bold text-white hover:border-white transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" /> Save
          </button>
          <button onClick={postNow} disabled={busy || !cfg.keys_set} data-testid="x-post-now-btn"
            title={cfg.keys_set ? "Post the next active advert now" : "Save your X API keys first"}
            className={`h-10 px-4 inline-flex items-center gap-2 rounded-full text-sm font-bold transition-colors ${cfg.keys_set && !busy ? "bg-white text-black hover:bg-neutral-200" : "bg-white/5 text-neutral-600 border border-[#27272A] cursor-not-allowed"}`}>
            <Send className="h-4 w-4" /> Post now
          </button>
          <button onClick={postFlyer} disabled={busy || !cfg.keys_set} data-testid="x-post-flyer-btn"
            title="Post the campaign flyer with the tagged Go-Pro link (cmp=x)"
            className={`h-10 px-4 inline-flex items-center gap-2 rounded-full text-sm font-bold transition-colors ${cfg.keys_set && !busy ? "bg-[#FF3B30] text-white hover:bg-[#e0342a]" : "bg-white/5 text-neutral-600 border border-[#27272A] cursor-not-allowed"}`}>
            <Send className="h-4 w-4" /> Post advert flyer
          </button>
        </div>
      </div>

      {(cfg.last_post_at || cfg.last_error) && (
        <div className="mt-3 text-xs font-mono" data-testid="x-last-status">
          {cfg.last_post_at && <span className="text-neutral-500">Last post: {new Date(cfg.last_post_at).toLocaleString()} (ad {cfg.last_posted_code})</span>}
          {cfg.last_error && <span className="text-[#FF3B30] ml-3">Error: {cfg.last_error}</span>}
        </div>
      )}
    </div>
  );
}
